import { type JSX } from 'react';
import starIcon from '@/assets/svg/static/star.svg';
import { useViewabilityTracker } from '@/hooks/useViewabilityTracker';
import {
  EVENTS,
  PROPERTIES,
  trackEvent,
  TRACKING_SOURCES,
} from '@/services/posthog';
import { updatedImageUrl } from '@/utils/formatting';
import { ReviewMediaThumbnail } from '../ReviewMediaThumbnail';
import styles from './ReviewCardWithTracking.module.css';
import type { ReviewCardWithTrackingProps } from './ReviewCardWithTracking.types';

export const ReviewCardWithTracking = ({
  review,
  productId,
  onReviewImageClick,
}: ReviewCardWithTrackingProps): JSX.Element => {
  const viewRef = useViewabilityTracker<HTMLDivElement>({
    onViewable: () => {
      trackEvent(EVENTS.PDP_V2_REVIEW_CARD_VIEWED, {
        [PROPERTIES.PRODUCT_ID]: productId,
        [PROPERTIES.USERNAME]: review.username,
        [PROPERTIES.RATING]: review.rating,
        [PROPERTIES.HAS_IMAGES]: !!(review.images && review.images.length > 0),
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });
    },
    threshold: 0.5,
    once: true,
  });

  return (
    <div ref={viewRef} className={styles.reviewCard}>
      <div className={styles.reviewUserRow}>
        <span className={styles.reviewUsername}>{review.username}</span>
        <span className={styles.reviewStars}>
          <span className={styles.ratingText}>{review.rating}</span>
          <img src={starIcon} alt="Star" className={styles.reviewStarIcon} />
        </span>
      </div>
      <p className={styles.reviewText}>{review.text}</p>
      {review.images && review.images.length > 0 && (
        <div className={styles.reviewCardPhotos}>
          {review.images.map((img, i, arr) => {
            const mediaUrl = updatedImageUrl(img);
            return (
              <ReviewMediaThumbnail
                key={i}
                src={mediaUrl}
                alt="Review Upload"
                className={styles.reviewCardPhoto}
                onClick={() => {
                  trackEvent(EVENTS.PDP_V2_CAROUSEL_IMAGE_CLICK, {
                    [PROPERTIES.PRODUCT_ID]: productId,
                    [PROPERTIES.IMAGE_INDEX]: i,
                    [PROPERTIES.IMAGE_URL]: mediaUrl,
                    [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
                  });
                  onReviewImageClick(
                    arr.map(updatedImageUrl),
                    i,
                    arr.map(() => review)
                  );
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
